/**
 * PhotoCollection2 layouts registry
 *
 * Exports all available layouts and the layout registry for type-safe selection.
 *
 * @module PhotoCollection2/layouts
 */

import { GridLayout } from './GridLayout';
import { MagazineLayout } from './MagazineLayout';
import { HorizontalLayout } from './HorizontalLayout';
import { VerticalLayout } from './VerticalLayout';
import type { PhotoCollection2LayoutComponent, LayoutConfig } from '../PhotoCollection2.types';

/**
 * Layout registry mapping names to components
 *
 * To add a new layout:
 * 1. Create layout component implementing PhotoCollection2LayoutProps
 * 2. Import it above
 * 3. Add to this registry
 * 4. TypeScript will automatically include in LayoutName union
 */
export const layouts: Record<string, PhotoCollection2LayoutComponent> = {
  'grid': GridLayout,
  'magazine': MagazineLayout,
  'horizontal': HorizontalLayout,
  'vertical': VerticalLayout,
};

/**
 * Layout configuration registry with metadata
 *
 * Maps layout names to configuration objects containing both the component
 * and metadata like scroll direction. Use this registry when parent components
 * need to make decisions based on layout behavior.
 *
 * To add a new layout:
 * 1. Create layout component implementing PhotoCollection2LayoutProps
 * 2. Import it above
 * 3. Add to both `layouts` and `layoutConfigs` registries
 * 4. Specify the correct scrollDirection ('vertical' or 'horizontal')
 */
export const layoutConfigs: Record<string, LayoutConfig> = {
  'grid': {
    component: GridLayout,
    scrollDirection: 'vertical',
  },
  'magazine': {
    component: MagazineLayout,
    scrollDirection: 'vertical',
  },
  'horizontal': {
    component: HorizontalLayout,
    scrollDirection: 'horizontal',
  },
  'vertical': {
    component: VerticalLayout,
    scrollDirection: 'vertical',
  },
};

/**
 * Extract layout names as literal union type
 * Enables TypeScript autocomplete and validation
 */
export type LayoutName = keyof typeof layouts;

// Re-export layouts for direct import
export { GridLayout, MagazineLayout, HorizontalLayout, VerticalLayout };
